(() => {
  if (customElements.get('gn-certificate-personalizer')) return;

  const designs = {
    original: { width: 2048, height: 1582, nameX: 1024, nameY: 785, nameWidth: 1140, nameSize: 88, dateX: 1276, dateY: 1091, dateWidth: 410, dateSize: 48, color: '#102a39', font: 'Gnome Original', sku: 'ORD-CERT-01' },
    fancy: { width: 1582, height: 2048, nameX: 795, nameY: 878, nameWidth: 990, nameSize: 94, dateX: 909, dateY: 1300, dateWidth: 340, dateSize: 43, color: '#26351e', font: 'Gnome Fancy', sku: 'ORD-CERT-03' },
    whimsical: { width: 2048, height: 1582, nameX: 1028, nameY: 778, nameWidth: 970, nameSize: 94, dateX: 1258, dateY: 1069, dateWidth: 405, dateSize: 49, color: '#232321', font: 'Gnome Whimsical', sku: 'ORD-CERT-02' },
  };

  const loadImage = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Artwork could not be loaded. Please try again.'));
    image.src = url;
  });

  const loadFont = async (family, url) => {
    if (!url) throw new Error('The certificate font is missing. Please try again later.');
    const face = new FontFace(family, `url("${url}") format("woff")`);
    try {
      await face.load();
      document.fonts.add(face);
    } catch {
      throw new Error('The certificate font could not be loaded. Please try again.');
    }
  };

  const formatDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '2024-01-15') return '';
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(date);
  };

  const drawText = (ctx, design, name, date) => {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = design.color;
    const fit = (text, width, maxSize, minSize) => {
      for (let size = maxSize; size >= minSize; size--) {
        ctx.font = `${size}px "${design.font}", Georgia, serif`;
        if (ctx.measureText(text).width <= width) return size;
      }
      return null;
    };
    if (name) {
      if (!fit(name, design.nameWidth, design.nameSize, 24)) throw new Error('This name does not fit on the certificate. Please shorten it.');
      ctx.fillText(name, design.nameX, design.nameY);
    }
    if (date) {
      if (!fit(date, design.dateWidth, design.dateSize, 25)) throw new Error('This date does not fit on the certificate.');
      ctx.fillText(date, design.dateX, design.dateY);
    }
  };

  const drawPreviewWatermark = (ctx, width, height) => {
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 6);
    ctx.globalCompositeOperation = 'difference';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.font = `700 ${Math.round(Math.min(width * 0.12, height * 0.16))}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const offset of [-height * 0.28, 0, height * 0.28]) {
      ctx.fillText('PREVIEW', 0, offset, width * 0.7);
    }
    ctx.restore();
  };

  class CertificatePersonalizer extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.nameInput = this.form?.querySelector('input[name="properties[Name to print]"]');
      this.dateInput = this.form?.querySelector('input[name="properties[Date of ordainment]"]');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.canvas = this.querySelector('canvas');
      this.error = this.querySelector('[role="alert"]');
      if (!this.form || !this.nameInput || !this.dateInput || !this.variantInput || !this.canvas) return;
      this.images = {};
      for (const key of Object.keys(designs)) {
        const label = key[0].toUpperCase() + key.slice(1);
        this.images[key] = {
          preview: loadImage(this.dataset[`preview${label}`]),
          font: loadFont(designs[key].font, this.dataset[`font${label}`]),
        };
        this.images[key].preview.catch(() => {});
        this.images[key].font.catch(() => {});
      }
      this.lastVariant = this.variantInput.value;
      this.drawPreview();
      this.nameInput.addEventListener('input', () => this.drawPreview());
      this.dateInput.addEventListener('input', () => this.drawPreview());
      this.variantPoll = setInterval(() => {
        if (this.variantInput.value !== this.lastVariant) {
          this.lastVariant = this.variantInput.value;
          this.drawPreview();
        }
      }, 250);
    }

    disconnectedCallback() {
      clearInterval(this.variantPoll);
    }

    selectedDesign() {
      const value = this.variantInput?.value;
      return Object.keys(designs).find((key) => this.dataset[`variant${key[0].toUpperCase()}${key.slice(1)}`] === value);
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async drawPreview() {
      const key = this.selectedDesign();
      if (!key) {
        this.showError('Please select a certificate design.');
        return;
      }
      const design = designs[key];
      const canvas = this.canvas;
      canvas.width = design.width;
      canvas.height = design.height;
      try {
        const [image] = await Promise.all([this.images[key].preview, this.images[key].font]);
        if (key !== this.selectedDesign()) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, design.width, design.height);
        ctx.drawImage(image, 0, 0, design.width, design.height);
        drawText(ctx, design, this.nameInput.value.trim().replace(/\s+/g, ' '), formatDate(this.dateInput.value));
        drawPreviewWatermark(ctx, design.width, design.height);
        this.showError('');
      } catch (err) {
        this.showError(err.message);
      }
    }

  }

  customElements.define('gn-certificate-personalizer', CertificatePersonalizer);
})();
